import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { MembershipBadge } from "@/components/MembershipBadge";
import { TrendingUp, FileText, MessageCircle, Image as ImageIcon, Bookmark, Trophy, Eye, Award } from "lucide-react";

function StatCard({ icon: Icon, label, value, sublabel, accent = false, testId }) {
    return (
        <div className={`ok-card p-5 ${accent ? "border-l-2" : ""}`} style={accent ? { borderLeftColor: "#e7c873" } : undefined} data-testid={testId}>
            <div className="flex items-center justify-between">
                <div className="font-mono text-[10px] uppercase tracking-widest opacity-60 flex items-center gap-1">
                    <Icon className="w-3 h-3" /> {label}
                </div>
            </div>
            <div className={`font-display text-3xl mt-1 ${accent ? "text-[#e7c873]" : ""}`}>{value}</div>
            {sublabel && <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mt-1">{sublabel}</div>}
        </div>
    );
}

export default function Analytics() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!authLoading && !user) navigate("/login");
    }, [user, authLoading, navigate]);

    useEffect(() => {
        if (user) {
            api.get("/me/analytics")
                .then((r) => setStats(r.data))
                .finally(() => setLoading(false));
        }
    }, [user]);

    if (!user) return null;
    if (loading || !stats) return <div className="ok-container py-12 font-mono text-xs opacity-50">Crunching numbers…</div>;

    const tier = stats.subscription_tier;
    const isCouncil = tier === "research_council";

    return (
        <div className="ok-container py-10 max-w-5xl" data-testid="analytics-page">
            <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mb-2">
                {isCouncil ? "Council Dashboard" : "Witness Dashboard"} · UNVEILED
            </div>
            <h1 className="font-display text-4xl flex items-center gap-3 flex-wrap">
                Research analytics
                {isCouncil && (
                    <span className="inline-flex items-center gap-1.5 ok-card px-2 py-0.5" style={{ borderColor: "#e7c873" }}>
                        <MembershipBadge tier="research_council" size="md" />
                        <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: "#e7c873" }}>Research Council</span>
                    </span>
                )}
                {tier === "verified_witness" && (
                    <span className="inline-flex items-center gap-1.5 ok-card px-2 py-0.5">
                        <MembershipBadge tier="verified_witness" size="md" />
                        <span className="font-mono text-[10px] uppercase tracking-widest opacity-80">Verified Witness</span>
                    </span>
                )}
            </h1>
            <p className="opacity-70 mt-2 text-sm leading-relaxed max-w-2xl">
                Your contribution snapshot across the UNVEILED archive. Council members receive deeper analytics; standard
                metrics shown for all signed-in witnesses.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6" data-testid="analytics-grid">
                <StatCard
                    icon={TrendingUp}
                    label="Contribution score"
                    value={stats.contribution_score}
                    sublabel="Composite metric"
                    accent={isCouncil}
                    testId="stat-contribution"
                />
                <StatCard icon={Trophy} label="Reputation" value={stats.reputation} sublabel="Earned from votes" testId="stat-reputation" />
                <StatCard icon={Eye} label="Profile views" value={stats.profile_views} sublabel="All time" testId="stat-views" />
                <StatCard icon={Award} label="Member tier" value={tier === "research_council" ? "Council" : tier === "verified_witness" ? "Witness" : "Free"} testId="stat-tier" />

                <StatCard icon={FileText} label="Encounters" value={stats.encounters.total} sublabel={`${stats.encounters.approved} approved · ${stats.encounters.pending} pending`} testId="stat-encounters" />
                <StatCard icon={MessageCircle} label="Comments" value={stats.comments.total} sublabel={`+${stats.comments.upvotes} / −${stats.comments.downvotes}`} testId="stat-comments" />
                <StatCard icon={ImageIcon} label="Media uploads" value={stats.media_uploads} sublabel="Library size" testId="stat-media" />
                <StatCard icon={Bookmark} label="Collections" value={stats.collections} sublabel="Curated sets" testId="stat-collections" />
            </div>

            <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mt-12 mb-3">Quick access</div>
            <div className="grid sm:grid-cols-3 gap-3">
                <Link to="/me/media" className="ok-card p-4 hover:border-[var(--ok-accent)] transition" data-testid="analytics-link-media">
                    <div className="font-mono text-[10px] uppercase tracking-widest opacity-60">Media library</div>
                    <div className="font-display text-lg mt-1">Manage uploads →</div>
                </Link>
                <Link to="/collections" className="ok-card p-4 hover:border-[var(--ok-accent)] transition" data-testid="analytics-link-collections">
                    <div className="font-mono text-[10px] uppercase tracking-widest opacity-60">Research collections</div>
                    <div className="font-display text-lg mt-1">Curate sets →</div>
                </Link>
                <Link to="/messages" className="ok-card p-4 hover:border-[var(--ok-accent)] transition" data-testid="analytics-link-messages">
                    <div className="font-mono text-[10px] uppercase tracking-widest opacity-60">Private channel</div>
                    <div className="font-display text-lg mt-1">Messages →</div>
                </Link>
            </div>

            {!isCouncil && (
                <div className="ok-card p-5 mt-10 flex items-start gap-4" style={{ borderColor: "#e7c873" }} data-testid="analytics-upsell">
                    <MembershipBadge tier="research_council" size="lg" />
                    <div className="flex-1">
                        <div className="font-display text-xl">Unlock the full Council dashboard</div>
                        <p className="text-sm opacity-80 mt-1">Research Council members get profile view tracking, comment elevation, larger uploads, and Trusted Source status across the platform.</p>
                        <Link to="/pricing" className="ok-btn-primary text-sm mt-3 inline-flex" data-testid="analytics-upsell-cta">Become Research Council →</Link>
                    </div>
                </div>
            )}
        </div>
    );
}
